import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useState } from 'react';
import { CircleCheck, Loader2, MessageCircle, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import './LoginPage.css';

const PURPOSE_COPY = {
  login: {
    zh: '扫码关注后登录',
    en: 'Scan, follow, and sign in',
  },
  bind: {
    zh: '扫码关注并绑定微信',
    en: 'Scan, follow, and bind WeChat',
  },
  reauth: {
    zh: '扫码完成身份验证',
    en: 'Scan to verify your identity',
  },
};

function copyFor(purpose, english) {
  const copy = PURPOSE_COPY[purpose] || PURPOSE_COPY.login;
  return english ? copy.en : copy.zh;
}

export default function WechatQrFlow({
  createSession,
  pollSession,
  onAuthorized,
  purpose = 'login',
  darkActive = false,
  english = false,
}) {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const purposeCopy = copyFor(purpose, english);

  useEffect(() => {
    let active = true;
    setSession(null);
    setStatus('loading');
    setMessage('');

    createSession()
      .then(nextSession => {
        const expiresAt = Date.parse(nextSession?.expires_at);
        if (!active) return;
        if (!nextSession?.session_id
          || !nextSession?.poll_token
          || typeof nextSession?.qr_content !== 'string'
          || !nextSession.qr_content
          || !Number.isFinite(expiresAt)
        ) {
          throw new Error('微信扫码会话数据不完整');
        }
        setSession(nextSession);
        setStatus('pending');
      })
      .catch(error => {
        if (!active) return;
        setStatus('failed');
        setMessage(getErrorMessage(error, '微信服务暂时不可用，请稍后重试。'));
      });

    return () => { active = false; };
  }, [createSession, english, refreshKey]);

  useEffect(() => {
    if (!session) return undefined;

    let active = true;
    let timer;
    const expiresAt = Date.parse(session.expires_at);
    const expire = () => {
      active = false;
      if (timer) window.clearTimeout(timer);
      setStatus('expired');
      setMessage('二维码已过期，请刷新后重新扫码。');
    };
    const schedule = () => {
      if (active) timer = window.setTimeout(poll, 2000);
    };
    const poll = async () => {
      if (!active) return;
      if (Date.now() >= expiresAt) {
        expire();
        return;
      }

      try {
        const result = await pollSession(session.session_id, session.poll_token);
        if (!active) return;
        if (result?.status === 'pending') {
          schedule();
          return;
        }
        if (result?.status === 'authorized') {
          setStatus('authorized');
          setMessage(english ? 'Authorized. Finishing…' : '授权成功，正在完成…');
          await onAuthorized(result);
          active = false;
          return;
        }
        if (result?.status === 'expired') {
          expire();
          return;
        }
        if (result?.status === 'failed') {
          active = false;
          setStatus('failed');
          setMessage(getErrorMessage(result.message, '微信授权未完成，请重试。'));
          return;
        }
        throw new Error('微信扫码状态异常');
      } catch (error) {
        if (!active) return;
        active = false;
        setStatus('failed');
        setMessage(getErrorMessage(error, '微信服务暂时不可用，请稍后重试。'));
      }
    };

    poll();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [english, onAuthorized, pollSession, session]);

  const retry = () => setRefreshKey(key => key + 1);
  const flowClass = `vnext-auth__wechat-flow${darkActive ? ' vnext-auth__wechat-flow--dark' : ''}`;

  if (status === 'failed' || status === 'expired') {
    const expired = status === 'expired';
    return (
      <div className={flowClass}>
        <div className="vnext-auth__qr mx-auto flex h-[230px] w-[230px] flex-col items-center justify-center rounded-[24px]">
          <RefreshCw size={25} strokeWidth={1.7} />
          <p className="mt-4 text-[13px] font-semibold">
            {expired
              ? ('二维码已过期')
              : ('授权未完成')}
          </p>
          <button type="button" onClick={retry} className="vnext-auth__qr-retry mt-4 rounded-full px-4 py-2 text-[11px] font-medium">
            {english ? 'Try again' : '重新生成'}
          </button>
        </div>
        {message && <p className="vnext-auth__error mt-3 text-center text-[11px] leading-5" role="alert">{message}</p>}
      </div>
    );
  }

  return (
    <div className={flowClass}>
      <div className="vnext-auth__wechat-frame mx-auto" aria-live="polite">
        {session && (
          <div className="vnext-auth__wechat-code">
            <QRCodeSVG
              value={session.qr_content}
              size={230}
              level="M"
              bgColor="#ffffff"
              fgColor="#17151a"
              includeMargin
              title={purposeCopy}
            />
          </div>
        )}
        {status === 'loading' && (
          <div className="vnext-auth__wechat-loading" role="status">
            <Loader2 size={22} className="animate-spin" />
            <span>{english ? 'Preparing QR code…' : '正在准备微信二维码…'}</span>
          </div>
        )}
        {status === 'authorized' && (
          <div className="vnext-auth__wechat-authorized" role="status">
            <CircleCheck size={30} strokeWidth={1.7} />
            <span>{message}</span>
          </div>
        )}
      </div>
      <div className="vnext-auth__wechat-guidance mt-4" role="status">
        <strong>{purposeCopy}</strong>
        <span>
          {status === 'pending' ? <MessageCircle size={12} /> : <RefreshCw size={12} />}
          {status === 'pending'
            ? (english ? 'Already following? Scan to continue directly.' : '已关注用户扫码后可直接继续')
            : (english ? 'The QR code is valid for 5 minutes.' : '二维码有效期为 5 分钟')}
        </span>
      </div>
    </div>
  );
}
